import { Card, CardContent, CardHeader, CardTitle } from "@/lib/shadcn/ui/card";
import { ArrowDownIcon } from "lucide-react";

const PayoutMechanismPage = () => {
    return (
        <div className="max-w-4xl mx-auto space-y-8">
            <div>
                <h1 className="text-3xl font-bold text-gray-900">Payout Mechanism</h1>
                <p className="text-gray-600 mt-2">How rental income flows from guest to owner</p>
            </div>

            {/* Flow Diagram */}
            <div className="flex flex-col items-center gap-2">
                <FlowStep title="Guest Pays" description="Total booking amount" color="bg-blue-50 border-blue-200" />
                <ArrowDownIcon className="h-6 w-6 text-gray-400" />
                <FlowStep title="Platform Receives" description="Airbnb, Booking.com, or Direct" color="bg-purple-50 border-purple-200" />
                <ArrowDownIcon className="h-6 w-6 text-gray-400" />
                <FlowStep title="Keros Receives" description="Gross income after platform commission" color="bg-amber-50 border-amber-200" />
                <ArrowDownIcon className="h-6 w-6 text-gray-400" />
                <FlowStep title="Tax Deductions" description="Sustainability Fee + Temporary Stay Duty + VAT" color="bg-red-50 border-red-200" />
                <ArrowDownIcon className="h-6 w-6 text-gray-400" />
                <FlowStep title="Net Income" description="Income after all deductions" color="bg-green-50 border-green-200" />
                <ArrowDownIcon className="h-6 w-6 text-gray-400" />
                <div className="flex flex-col sm:flex-row gap-4 w-full max-w-lg">
                    <FlowStep title="Owner Payout" description="Net income x payout ratio" color="bg-emerald-50 border-emerald-200" />
                    <FlowStep title="Management Fee" description="Net income x management fee ratio" color="bg-slate-50 border-slate-200" />
                </div>
            </div>

            {/* Detail Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Gross Income</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-gray-600 space-y-2">
                        <p>
                            The <strong>gross income</strong> (or receivable income) is the total amount that Keros receives from the booking platform
                            after the platform has taken its commission.
                        </p>
                        <p>For direct bookings (outside platforms), the full booking amount is the gross income.</p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Platform Commissions</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-gray-600 space-y-2">
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <span className="inline-block w-3 h-3 rounded-full bg-red-400" />
                                <strong>Airbnb:</strong> ~3% host commission (deducted before payout)
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="inline-block w-3 h-3 rounded-full bg-blue-400" />
                                <strong>Booking.com:</strong> ~15% commission (deducted from guest payment)
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="inline-block w-3 h-3 rounded-full bg-green-400" />
                                <strong>Direct:</strong> No platform commission
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Tax Deductions</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-gray-600 space-y-3">
                        <div>
                            <strong>Sustainability Fee (Climate Crisis Resilience Fee)</strong>
                            <p>A per-night fee collected from guests, remitted to the tax authority.</p>
                        </div>
                        <div>
                            <strong>Temporary Stay Duty</strong>
                            <p>A municipal levy based on the property location and guest count.</p>
                        </div>
                        <div>
                            <strong>VAT</strong>
                            <p>Value Added Tax applied to short-term rental income.</p>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Net Income & Payouts</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-gray-600 space-y-3">
                        <div className="p-3 bg-gray-50 rounded-md font-mono text-xs">
                            <p>Net Income = Gross Income - Commission - Taxes - Cleaning Fees</p>
                        </div>
                        <p>
                            The net income is then split between the <strong>owner payout</strong> (60-80%) and the{" "}
                            <strong>management fee</strong> (20-40%) based on the agreed ratios.
                        </p>
                        <div className="space-y-2 mt-2">
                            <div className="flex items-start gap-2">
                                <span className="text-emerald-600 font-semibold whitespace-nowrap">80% payout:</span>
                                <span className="text-sm">
                                    The owner handles all property expenses (utility bills, hosting, maintenance) on their name.
                                </span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-emerald-600 font-semibold whitespace-nowrap">60% payout:</span>
                                <span className="text-sm">
                                    20% is retained by Keros to cover all property expenses (utility bills, hosting, maintenance).
                                    In this case, the property owner is taxed on real income received!
                                </span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

const FlowStep = ({ title, description, color }: { title: string; description: string; color: string }) => (
    <div className={`w-full max-w-lg border rounded-lg p-4 text-center ${color}`}>
        <div className="font-semibold text-gray-900">{title}</div>
        <div className="text-sm text-gray-600">{description}</div>
    </div>
);

export default PayoutMechanismPage;
